import { Button, IButtonProps } from "azure-devops-ui/Button";
import { ContentJustification, ContentLocation, ContentOrientation } from "azure-devops-ui/Callout";
import { FocusZone, FocusZoneDirection } from "azure-devops-ui/FocusZone";
import { IReadonlyObservableValue } from "azure-devops-ui/Core/Observable";
import { Icon } from "azure-devops-ui/Icon";
import { Link } from "azure-devops-ui/Link";
import { ITooltipProps, Tooltip } from "azure-devops-ui/TooltipEx";
import { css } from "azure-devops-ui/Util";
import React from "react";
import { useHistory } from "react-router-dom";
import { Callout } from "../../common/components/callout/callout";
import { Observer } from "../../common/components/observer/observer";
import { EventContext } from "../../common/contexts/event";
import { NavigationContext } from "../../common/contexts/navigation";
import { format, formatComponent } from "../../common/utilities/format";
import * as Icons from "../components/illustration/icons";
import { SessionContext } from "../contexts/session";
import { IAccount } from "../types/account";
import { formatQuotaSize, formatSize } from "../utilities/format";

import "./navigation.css";

const { BuyStorageButton, ToggleNavigation } = window.Resources.Common;
const {
  ExpandCollapseAltLabel,
  FilesPage,
  FilesPageAltLabel,
  GoPremium,
  LearnMoreStorageLink,
  OneDriveAppLink,
  PhotosPage,
  PhotosPageAltLabel,
  PurchaseLabel,
  QuotaPercentage,
  QuotaUsage,
  QuotaUsageOver,
  RecentPage,
  RecentPageAltLabel,
  RecycleBinPage,
  RecycleBinPageAltLabel,
  SharedPage,
  SharedPageAltLabel,
  StorageLabel
} = window.Resources.Navigation;

export interface INavigationProps {
  accountDetails: IReadonlyObservableValue<IAccount | undefined>;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

export function Navigation(props: INavigationProps): React.ReactElement {
  const { accountDetails, expanded, setExpanded } = props;

  const navigationContext = React.useContext(NavigationContext);
  const sessionContext = React.useContext(SessionContext);
  const eventContext = React.useContext(EventContext);

  // Track the router and use it for FPS navigations between pages.
  const username = sessionContext.userProfile && (sessionContext.userProfile.name || sessionContext.userProfile.preferred_username);

  return (
    <nav className={css("photo-navigation flex-column flex-noshrink overflow-hidden padding-bottom-12", expanded ? "expanded" : "collapsed")}>
      <div className="flex-column overflow-hidden">
        <NavigationButton
          ariaExpanded={expanded}
          ariaLabel={ExpandCollapseAltLabel}
          className="toggle-navigation flex-self-start"
          expanded={true}
          id="expand"
          iconProps={{ iconName: "GlobalNavButton" }}
          onClick={(event) => {
            eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "toggleNavigation", value: !expanded });

            setExpanded(!expanded);
            event.preventDefault();
          }}
          tooltipProps={{ showOnFocus: true, text: ToggleNavigation }}
        />
        {expanded && username && (
          <Tooltip overflowOnly={true}>
            <span className="navigation-user-details flex-row flex-center flex-noshrink font-weight-semibold text-ellipsis">{username}</span>
          </Tooltip>
        )}
        <FocusZone direction={FocusZoneDirection.Vertical} handleTabKey={false}>
          <div
            aria-label={format("{{username}}, use the up and down keys to move through different sub links; hit enter to navigate to a sub link.", {
              username
            })}
            className={css("flex-column overflow-x-hidden overflow-y-auto", !expanded && "scrollbar-hidden")}
            data-testid="navigation-links"
            role="menu"
          >
            <NavigationLink
              ariaLabel={FilesPageAltLabel}
              expanded={expanded}
              href="https://onedrive.live.com"
              iconProps={{ render: (className: string) => <Icons.Folder className={className} /> }}
              onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigateMyFiles" })}
              text={FilesPage}
            />
            <NavigationLink
              ariaLabel={RecentPageAltLabel}
              excludeTabStop={true}
              expanded={expanded}
              href="https://onedrive.live.com?qt=mru"
              iconProps={{ render: (className: string) => <Icons.Recent className={className} /> }}
              onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigateRecent" })}
              text={RecentPage}
            />
            <NavigationLink
              ariaLabel={PhotosPageAltLabel}
              excludeTabStop={true}
              expanded={expanded}
              href="/"
              iconProps={{ render: (className: string) => <Icons.Photo className={className} /> }}
              onClick={(event) => navigationContext.navigate(event, "navigateGallery")}
              selected={true}
              text={PhotosPage}
            />
            <NavigationLink
              ariaLabel={SharedPageAltLabel}
              excludeTabStop={true}
              expanded={expanded}
              href="https://onedrive.live.com?qt=sharedby"
              iconProps={{ render: (className: string) => <Icons.People className={className} /> }}
              onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigateShared" })}
              text={SharedPage}
            />
            <NavigationLink
              ariaLabel={RecycleBinPageAltLabel}
              excludeTabStop={true}
              expanded={expanded}
              href="https://onedrive.live.com?qt=recyclebin"
              iconProps={{ render: (className: string) => <Icons.Recyclebin className={className} /> }}
              onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigateRecycleBin" })}
              text={RecycleBinPage}
            />
          </div>
        </FocusZone>
      </div>
      <div className="flex-column flex-grow flex-justify-end rhythm-vertical-8 padding-top-8">
        <Observer values={{ accountDetails }}>
          {({ accountDetails }) => {
            // If we don't have any account details we don't render anything in this section.
            if (accountDetails && accountDetails.quota) {
              const { quota, status, unifiedStorageQuota } = accountDetails;
              const { remaining, state, total, used } = unifiedStorageQuota || quota;
              const { storagePlans } = accountDetails.quota;

              // Determine the state of the user based on the account details.
              const full = used >= total;
              const locked = status?.state === "lockedDown";
              const quotaPercentage = Math.floor((used / total) * 100);
              const warningState = state !== "normal";

              // Produce the quota message that describes the percentage full.
              const percentageMessage = format(QuotaPercentage, { quotaPercentage });

              // Determine which resource we will use for the usage details.
              const usageResource = full ? QuotaUsageOver : QuotaUsage;

              return (
                <div className={css("account-details flex-column margin-bottom-16", warningState && "warning", full && "full")}>
                  {expanded && !warningState && storagePlans.upgradeAvailable && (
                    <div className="flex-column padding-top-8 margin-bottom-8">
                      <span className="purchase-label flex-row body-s margin-horizontal-16 secondary-text">{PurchaseLabel}</span>
                      <Link
                        className="flex-row body-s margin-horizontal-16 margin-bottom-8"
                        href="https://onedrive.live.com/?v=upgrade&hideLeftNav=true&ocid=PROD%5FOneDrive%2DWeb%5FPremiumLeftNav%5FNormal%5FGetMoreStorage"
                      >
                        {LearnMoreStorageLink}
                      </Link>
                    </div>
                  )}
                  {warningState ? (
                    !expanded ? (
                      <Tooltip text={percentageMessage}>
                        <div className="flex-row flex-justify-center margin-bottom-8">
                          {locked ? <Icons.SubtractionCircle className="quota-icon" /> : <Icons.Warning className="quota-icon" />}
                        </div>
                      </Tooltip>
                    ) : null
                  ) : (
                    <NavigationButton
                      className={css("premium-button", expanded ? "margin-bottom-16 margin-horizontal-16" : "margin-bottom-8 flex-self-center")}
                      expanded={expanded}
                      href="https://onedrive.live.com/?v=upgrade&hideLeftNav=true&hideLeftNav=true&ocid=PROD_OneDrive-Web_PremiumLeftNav_Normal_GetMoreStorage"
                      iconProps={{ render: (className: string) => <Icons.Diamond className={className} /> }}
                      onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigatePremium" })}
                      text={GoPremium}
                    />
                  )}
                  {expanded && (
                    <div className="flex-row flex-align-center margin-horizontal-16 margin-bottom-12">
                      <span className="quota-icon-label font-weight-semibold">{StorageLabel}</span>
                    </div>
                  )}
                  <div
                    aria-label={percentageMessage}
                    className={css("flex-row margin-bottom-4", expanded ? "margin-horizontal-16" : "margin-horizontal-8")}
                  >
                    <div className="quota-used-percentile flex-noshrink" style={{ width: Math.max(1, Math.min(100, quotaPercentage)) + "%" }} />
                    <div className="quota-unused-percentile flex-grow" />
                  </div>
                  {expanded && (
                    <span
                      className={css(
                        "body-s secondary-text white-space-pre-wrap overflow-hidden margin-horizontal-16",
                        warningState && "margin-bottom-8"
                      )}
                    >
                      {formatComponent(
                        format(usageResource, {
                          overage: formatSize(used - total),
                          quotaPercentage,
                          remaining: formatSize(remaining),
                          total: formatQuotaSize(total),
                          used: formatQuotaSize(used)
                        }),
                        (content) => (
                          <Link
                            className={css("quota-used-text", full && "underline")}
                            href="https://onedrive.live.com/?v=managestorage"
                            key="manage-storage"
                            onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "manageStorage" })}
                          >
                            {content}
                          </Link>
                        )
                      )}
                    </span>
                  )}
                  {expanded && warningState && (
                    <Button
                      className="buy-storage margin-horizontal-16"
                      href="https://onedrive.live.com/?v=upgrade&hideLeftNav=true&ocid=PROD_OneDrive-Web_LeftNav_Full_GetMoreStorage"
                      primary={true}
                    >
                      {BuyStorageButton}
                    </Button>
                  )}
                </div>
              );
            }

            return null;
          }}
        </Observer>
        {expanded ? (
          <Link
            className="body-s font-weight-semibold margin-horizontal-16 padding-top-4"
            href="https://g.live.com/8SESkyDrive/SkyDriveApps?biciid=lhnlink"
            onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigateGetApps" })}
          >
            {OneDriveAppLink}
          </Link>
        ) : (
          <NavigationButton
            className="flex-self-center"
            href="https://g.live.com/8SESkyDrive/SkyDriveApps?biciid=lhnlink"
            iconProps={{
              render: () => <Icons.OneDrive />
            }}
            onClick={() => eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "navigateGetApps" })}
            text={OneDriveAppLink}
          />
        )}
      </div>
    </nav>
  );
}

export interface INavigationPanelProps {
  accountDetails: IReadonlyObservableValue<IAccount | undefined>;
  onDismiss: () => void;
}

export function NavigationPanel(props: INavigationPanelProps): React.ReactElement {
  const { accountDetails, onDismiss } = props;

  // If navigation occurs we need to dismiss the navigation.
  const history = useHistory();

  // We need to dismiss the dialog if we navigate.
  React.useEffect(() => {
    const unregisterCallback = history.listen(() => onDismiss());
    return () => {
      unregisterCallback();
    };
  });

  return (
    <Callout
      className="navigation-callout absolute-fill"
      contentClassName="flex-column"
      contentJustification={ContentJustification.Stretch}
      contentLocation={ContentLocation.Start}
      contentOrientation={ContentOrientation.Column}
      focuszoneProps={{
        circularNavigation: true,
        focusOnMount: true,
        handleTabKey: true,
        includeDefaults: true
      }}
      escDismiss={true}
      id="navigation-panel"
      lightDismiss={true}
      modal={true}
      onDismiss={onDismiss}
    >
      <Navigation accountDetails={accountDetails} expanded={true} setExpanded={onDismiss} />
    </Callout>
  );
}

export interface INavigationButtonProps extends IButtonProps {
  expanded?: boolean;
  selected?: boolean;
}

export function NavigationButton(props: INavigationButtonProps): React.ReactElement {
  const { ariaLabel, className, expanded, selected, text, ...buttonProps } = props;

  return (
    <Button
      ariaLabel={ariaLabel || (!expanded ? text : undefined)}
      className={css("navigation-button flex-noshrink", className, expanded && "expanded", selected && "selected")}
      subtle={true}
      {...buttonProps}
    >
      {expanded && text ? (
        <Tooltip overflowOnly={true}>
          <span className="navigation-button-text text-ellipsis">{text}</span>
        </Tooltip>
      ) : null}
    </Button>
  );
}

export interface INavigationLinkProps extends IButtonProps {
  expanded?: boolean;
  selected?: boolean;
}

export function NavigationLink(props: INavigationLinkProps): React.ReactElement {
  const { ariaLabel, className, expanded, iconProps, selected, text, ...buttonProps } = props;

  // Since this doesnt seem to be an iconOnly button when collapsed we will add
  // the tooltip configuration manually.
  const tooltipProps: ITooltipProps | undefined = !expanded ? { text } : undefined;

  return (
    <Button
      ariaLabel={ariaLabel}
      className={css(
        "navigation-link",
        className,
        "navigation-item flex-justify-start flex-row flex-noshrink",
        expanded && "expanded",
        selected && "selected"
      )}
      role="menuitem"
      subtle={true}
      tooltipProps={tooltipProps}
      {...buttonProps}
    >
      {selected && <div className="selection-banner margin-vertical-4" />}
      {iconProps && <Icon {...iconProps} className={css(iconProps.className, "left-icon")} />}
      {expanded && text ? (
        <Tooltip overflowOnly={true}>
          <span className="navigation-link-text text-ellipsis">{text}</span>
        </Tooltip>
      ) : null}
    </Button>
  );
}
